<template>
  <v-menu
    v-if="hasAnyItemsInMenu"
    v-model="showingMenu"
    :target="[positionX, positionY]"
    :close-on-content-click="false"
    :persistent="false"
    location="bottom"
    absolute
    eager
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" />
    </template>

    <v-list width="250px" data-cy="list-podItem-actions-list">
      <v-list-item
        v-if="$canControlNodeAndPod()"
        @click="openBadgeStatusMenu"
        ref="badgeStatusTarget"
        prepend-icon="mdi-state-machine"
      >
        <v-list-item-title>{{ $t('routes.pod.setBadgeStatus') }}</v-list-item-title>
        <v-list-item-subtitle>{{ getBadgeStatusText }}</v-list-item-subtitle>
      </v-list-item>

      <v-list-item
        v-if="isAdobeExtension && isSupportedByAP"
        @click="openAdobePremiereDialog"
        prepend-icon="mdi-export"
      >
        <v-list-item-title>
          {{ $t('routes.node.adobePremiereAdd') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$isFCPExtension() && isSupportedByFCP"
        @click="openFinalCutProDialog"
        prepend-icon="mdi-export"
      >
        <v-list-item-title>
          {{ $t('routes.node.FCPAdd') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canDownloadNodeAndPod() && !$isAdobeExtension() && !$isFCPExtension()"
        @click="openDownloadDialog"
        data-cy="list-podItem-actions-downloadPod"
        prepend-icon="mdi-download-box-outline"
      >
        <v-list-item-title>{{ $t('routes.node.downloadPod') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canDownloadNodeAndPod() && !$isAdobeExtension() && !$isFCPExtension()"
        @click="downloadNote"
        data-cy="list-podItem-actions-downloadNote"
        prepend-icon="mdi-note-text-outline"
      >
        <v-list-item-title>{{ $t('routes.pod.downloadNote') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canControlNodeAndPod()"
        @click="openRenamePodDialog"
        data-cy="list-podItem-actions-renamePod"
        prepend-icon="mdi-form-textbox"
      >
        <v-list-item-title>{{ $t('routes.node.renamePod') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canControlNodeAndPod()"
        @click="openCopySelectedDialog"
        data-cy="list-podItem-actions-copyPod"
        prepend-icon="mdi-checkbox-multiple-marked-outline"
      >
        <v-list-item-title>{{ $t('routes.node.copyPod') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canControlNodeAndPod() && !asSearchResult"
        @click="openMoveSelectedDialog"
        data-cy="list-podItem-actions-movePod"
        prepend-icon="mdi-checkbox-multiple-marked"
      >
        <v-list-item-title>{{ $t('routes.node.movePod') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canDeleteNodeAndPod() && !asSearchResult"
        @click="openDeletePodDialog"
        data-cy="list-podItem-actions-deletePod"
        prepend-icon="mdi-trash-can-outline"
      >
        <v-list-item-title>{{ $t('routes.node.deletePod') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="$canRunFreeService() && isArchivePod"
        @click="openExtractArchiveDialog"
        data-cy="list-podItem-actions-extractArchive"
        prepend-icon="mdi-archive-cog-outline"
      >
        <v-list-item-title>{{ $t('routes.pod.extractArchiveShort') }}</v-list-item-title>
      </v-list-item>

      <v-divider aria-hidden="true" />

      <v-list-item v-if="$isTeamUser() && $canControlNodeAndPod()" @click="toggleTeamonly()">
        <template v-slot:prepend>
          <v-icon v-if="item.teamonly" size="small">mdi-file-lock-open</v-icon>
          <v-icon v-if="!item.teamonly" size="small">mdi-file-lock-outline</v-icon>
        </template>
        <v-list-item-title>
          {{ item.teamonly ? $t('routes.node.removeTeamOnlyAccess') : $t('routes.node.setTeamOnlyAccess') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item v-if="$canViewPodAndNodeAnalytics()" @click="openPodAnalyticsDialog" prepend-icon="mdi-poll">
        <v-list-item-title>{{ $t('routes.pod.viewAnalytics') }}</v-list-item-title>
      </v-list-item>

      <v-list-item @click="openPodPropertiesDialog" prepend-icon="mdi-information-outline">
        <v-list-item-title>{{ $t('routes.node.properties') }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>

  <div>
    <PodBadgeStatusMenu
      v-if="$canControlNodeAndPod()"
      v-model="showingBadgeStatusMenu"
      :item="item"
      :target="$refs.badgeStatusTarget"
    />

    <AdobePremiereDialog
      v-if="isAdobeExtension && isSupportedByAP"
      :pods="[item]"
      v-model="showAdobePremiereDialog"
    />

    <FinalCutProDialog
      v-if="$isFCPExtension() && isSupportedByFCP"
      :pods="[item]"
      v-model="showFinalCutProDialog"
    />

    <DownloadDialog
      v-if="$canDownloadNodeAndPod() && !$isAdobeExtension() && !$isFCPExtension()"
      :pods="Array(item)"
      v-model="showDownloadDialog"
      @menu-close="showingMenu = false"
    />

    <RenamePodDialog
      v-if="$canControlNodeAndPod()"
      :pod="item"
      v-model="showRenamePodDialog"
    />

    <CopySelectedDialog
      v-if="$canControlNodeAndPod()"
      :nodes="[]"
      :pods="new Array(item)"
      v-model="showCopySelectedDialog"
    />

    <MoveSelectedDialog
      v-if="$canControlNodeAndPod() && !asSearchResult"
      v-model="showMoveSelectedDialog"
      :nodes="[]"
      :pods="new Array(item)"
    />

    <DeletePodDialog
      v-if="$canDeleteNodeAndPod() && !asSearchResult"
      v-model="showDeletePodDialog"
      :pod="item"
    />

    <ExtractArchiveDialog
      v-if="$canRunFreeService() && isArchivePod"
      v-model="showExtractArchiveDialog"
      :pod="item"
    />

    <PodPropertiesDialog
      v-model="showPodPropertiesDialog"
      :pod="item"
    />

    <PodAnalyticsDialog
      v-model="showPodAnalyticsDialog"
      :pod="item"
    />
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import DownloadDialog from "../dialogs/DownloadDialog.vue";
import DeletePodDialog from "../dialogs/DeletePodDialog.vue";
import RenamePodDialog from "../dialogs/RenamePodDialog.vue";
import MoveSelectedDialog from "../dialogs/MoveSelectedDialog.vue";
import CopySelectedDialog from "../dialogs/CopySelectedDialog.vue";
import AdobePremiereDialog from "../dialogs/AdobePremiereDialog.vue";
import FinalCutProDialog from "../dialogs/FinalCutProDialog.vue";
import PodPropertiesDialog from "../dialogs/PodPropertiesDialog.vue";
import ExtractArchiveDialog from "../dialogs/ExtractArchiveDialog.vue";
import MimetypeHelper from '@/components/MimetypeHelper.js';
import PodBadgeStatusMenu from "./PodBadgeStatusMenu.vue";
import PodAnalyticsDialog from '@/routes/pods2/components/dialogs/PodAnalyticsDialog.vue';

export default {
  emits: ["update:modelValue"],
  name: "PodContextMenu",

  components: {
    DownloadDialog,
    DeletePodDialog,
    RenamePodDialog,
    MoveSelectedDialog,
    CopySelectedDialog,
    AdobePremiereDialog,
    FinalCutProDialog,
    PodPropertiesDialog,
    ExtractArchiveDialog,
    PodBadgeStatusMenu,
    PodAnalyticsDialog,
  },

  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    asSearchResult: {
      type: Boolean,
      default: false,
    },
    positionX: {
      type: Number,
      required: true,
    },
    positionY: {
      type: Number,
      required: true,
    },
    item: {
      type: Object,
      // required: true,
    },
  },

  data() {
    return {
      showingBadgeStatusMenu: false,
      showAdobePremiereDialog: false,
      showFinalCutProDialog: false,
      showDownloadDialog: false,
      showRenamePodDialog: false,
      showCopySelectedDialog: false,
      showMoveSelectedDialog: false,
      showDeletePodDialog: false,
      showExtractArchiveDialog: false,
      showPodPropertiesDialog: false,
      showPodAnalyticsDialog: false,
    };
  },

  watch: {
    podFromState() {
      if (this.podFromState && this.podFromState.archived !== this.archiveStatus.ARCHIVED) {
        this.showingMenu = false;
      }
    },
  },

  methods: {
    openBadgeStatusMenu() {
      this.showingBadgeStatusMenu = true;
    },
    openAdobePremiereDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showAdobePremiereDialog = true;
      });
    },
    openFinalCutProDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showFinalCutProDialog = true;
      });
    },
    openDownloadDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showDownloadDialog = true;
      });
    },
    downloadNote() {
      const commonStore = useCommonStore();

      this.showingMenu = false;
      commonStore.downloadMediaNotes({
        nodeids: [],
        podids: [this.item.id],
      });
    },
    openRenamePodDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showRenamePodDialog = true;
      });
    },
    openCopySelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showCopySelectedDialog = true;
      });
    },
    openMoveSelectedDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showMoveSelectedDialog = true;
      });
    },
    openDeletePodDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showDeletePodDialog = true;
      });
    },
    openExtractArchiveDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showExtractArchiveDialog = true;
      });
    },
    openPodPropertiesDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showPodPropertiesDialog = true;
      });
    },
    openPodAnalyticsDialog() {
      this.showingMenu = false;
      this.$nextTick(() => {
        this.showPodAnalyticsDialog = true;
      });
    },
    handleBadgeStatusChange(statusText) {
      this.badgeStatusText = statusText;
    },
    toggleTeamonly() {
      const data = {
        id: this.item.id,
        teamonly: !this.item.teamonly ? 1 : 0,
      };

      const podStore = usePodStore();
      podStore.updateTeamOnly(data)
        .then(() => {
          this.$showMessage(this.$t('routes.pod.teamonlyAccessUpdated'), { type: 'success' });
        })
        .catch((error) => {
          if (error.response?.data?.error?.code) {
            const errorCode = `backend.${error.response.data.error.code}`;
            this.$showMessage(this.$t(errorCode), { type: 'error' });
          } else if (error && error.message) {
            this.$showMessage(error.message, { type: 'error' });
          } else {
            console.log(error);
            this.$showMessage(this.$t('backend.system-error'), { type: 'error' });
          }
        });

      this.showingMenu = false;
    },
    stub() {
      this.showingMenu = false;
    },
  },

  computed: {
    showingMenu: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    archiveStatus() {
      const commonStore = useCommonStore();
      return commonStore.archiveStatus;
    },
    podFromState() {
      if (!this.item) {
        return;
      }

      const podStore = usePodStore();
      return podStore.getPods.filter((x) => x.id === this.item.id);
    },
    hasAnyItemsInMenu() {
      return true;
    },
    isAdobeExtension() {
      if (window.cep) {
        return true;
      }
      return false;
    },
    isArchivePod() {
      return MimetypeHelper.isMimetypeArchive(this.item);
    },
    isSupportedByFCP() {
      return MimetypeHelper.isSupportedByFCP(this.item);
    },
    isSupportedByAP() {
      return MimetypeHelper.isSupportedByAP(this.item);
    },
    currentNodeId() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNodeId;
    },
    hasSelectedItems() {
      const nodeStore = useNodeStore();
      const podStore = usePodStore();

      return nodeStore.hasSelectedNodes || podStore.hasSelectedPods;
    },
    badgeStatuses: function() {
      const commonStore = useCommonStore();
      return commonStore.podBadgeStatuses;
    },
    getBadgeStatusText: function() {
      if (!this.item) {
        return '';
      }

      switch (this.item.badgestatus) {
        case this.badgeStatuses.NOTSELECTED: return this.$t('routes.pod.badgeStatusNotSelected');
        case this.badgeStatuses.NEW: return this.$t('routes.pod.badgeStatusNew');
        case this.badgeStatuses.DRAFT: return this.$t('routes.pod.badgeStatusDraft');
        case this.badgeStatuses.PRODUCTION: return this.$t('routes.pod.badgeStatusProduction');
        case this.badgeStatuses.REVIEW: return this.$t('routes.pod.badgeStatusReview');
        case this.badgeStatuses.CORRECTION: return this.$t('routes.pod.badgeStatusCorrection');
        case this.badgeStatuses.FINAL: return this.$t('routes.pod.badgeStatusFinal');
        default: return '';
      }
    },
  }
};
</script>

<style>

</style>
